import React, { useState } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDropzone } from 'react-dropzone';

/**
 * `width` and `height` are real props rather than something to reach for `sx`
 * for, because this component is used at two quite different sizes: a big
 * drop target on a settings tab, and a small square thumbnail sitting in a
 * toolbar. The toolbar case used to pass Chakra-era `h` / `mt` props left
 * over from the migration, which React handed straight to the DOM — so the
 * control kept its 300px full-width default in a row that had space for
 * about 90, and the row grew a tall empty strip around it.
 */
export default function Dropzone(props) {
  const { content, onFileUploaded, file, sx, width, height, ...rest } = props;
  const [preview, setPreview] = useState(file ? file : null);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop: (acceptedFiles) => {
      const previewUrl = URL.createObjectURL(acceptedFiles[0]);
      setPreview(previewUrl);
      onFileUploaded(acceptedFiles[0]);
    },
    noClick: true,
    noKeyboard: true,
  });

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setPreview(null);
    onFileUploaded(null);
  };

  return (
    <Box
      {...getRootProps({ className: 'dropzone' })}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'action.hover',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: '16px',
        width: width || '100%',
        maxWidth: '100%',
        height: height || { xs: '208px', xl: '300px' },
        flexShrink: 0,
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        ...sx,
      }}
      {...rest}
    >
      <input {...getInputProps()} />
      {preview ? (
        <>
          <Box
            component='img'
            src={preview}
            alt='Preview'
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <IconButton
            onClick={handleRemoveImage}
            sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'background.paper', zIndex: 1 }}
            size='small'
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </>
      ) : (
        <Button onClick={open} sx={{ textTransform: 'none' }}>
          {content}
        </Button>
      )}
    </Box>
  );
}
